class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form[role="search"]');
    this.results = this.querySelector('.predictive-search-results');
    this.overlay = this.querySelector('.predictive-search-overlay');

    this.abortController = null;
    this.debounceTimeout = null;

    this.onInput = this.onInput.bind(this);
    this.onResultsClick = this.onResultsClick.bind(this);
    this.resetResults = this.resetResults.bind(this);
  }

  connectedCallback() {
    if (this.results) {
      this.results.addEventListener('click', this.onResultsClick);
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.resetResults);
    }

    if (!this.input) return;

    this.input.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    if (this.results) {
      this.results.removeEventListener('click', this.onResultsClick);
    }

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.resetResults);
    }

    if (!this.input) return;

    this.input.removeEventListener('input', this.onInput);

    if (this.abortController) {
      this.abortController.abort();
    }

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }

  onResultsClick(event) {
    const closeButton = event.target.closest('[data-predictive-search-close]');

    if (!closeButton) return;

    this.resetResults();
  }

  resetResults() {
    if (!this.results) return;

    this.results.innerHTML = '';
    this.results.dataset.active = 'false';
    document.body.setAttribute('nav-open', 'false');

    if (this.input) {
      this.input.focus();
    }
  }

  onInput(event) {
    const query = event.target.value.trim();

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    if (!query.length) {
      this.resetResults();
      return;
    }

    // Simple debounce so we don't spam the API while typing
    this.debounceTimeout = setTimeout(() => {
      this.fetchSuggestions(query);
    }, 250);
  }

  fetchSuggestions(query) {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,query',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,variants.sku',
      section_id: 'predictive-search',
    });

    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${base}search/suggest?${params.toString()}`;

    let requestResponse;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        requestResponse = response;
        return response.text();
      })
      .then((text) => {
        if (!requestResponse.ok) {
          throw new Error(`${requestResponse.status}: ${text}`);
        }

        if (!this.results) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');

        if (!section) return;

        const markup = section.innerHTML || '';
        this.results.innerHTML = markup;
        this.results.dataset.active = markup.trim().length ? 'true' : 'false';
        document.body.setAttribute('nav-open', 'true');
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        console.error('Predictive search error:', error);
      });
  }
}

customElements.define('predictive-search', PredictiveSearch);